import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button, Icon } from '@column/column-ui-kit';

import { buildCreateRequestForEntitiesAndAccountsSubset } from '../utils';
import { StateTabs } from '~/app/Layout';
import { PageActions } from '~/app/Layout/PageFrameContext';
import { RoleInfo } from '~/app/pages/Platform/Roles/_partials/RoleInfo';
import { ROUTE } from '~/app/routes';
import { useCreateCustomRole, useUpdateCustomRole } from '~/hooks/useRoles';
import { useNavigate } from '~/lib/navigation';
import PermissionConfiguration, { Mode, PermissionConfigurationSection, useEntitiesAndAccounts } from '~/lib/roles';
import { RoleRepository } from '~/repositories/RoleRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { CreateCustomRoleRequestObject } from '~/typings/API';

enum Section {
  Info = 'role-info',
  Transfers = 'role-transfers-and-accounts',
  Additional = 'role-additional-permissions',
}

const sections = [
  { label: 'Role Info', value: Section.Info },
  { label: 'Transfers & Accounts', value: Section.Transfers },
  { label: 'Additional Permissions', value: Section.Additional },
];
const sectionValues = sections.map((section) => section.value);

const trimDefaults = (data: CreateCustomRoleRequestObject): CreateCustomRoleRequestObject => {
  data.platformLevelPermissions = Object.fromEntries(
    Object.entries(data.platformLevelPermissions).filter(([, v]) => v !== 'default')
  );
  data.entityLevelOverrides = (data.entityLevelOverrides ?? [])
    .map((entity) => {
      return Object.fromEntries(Object.entries(entity).filter(([k, v]) => k === 'entityId' || v !== 'default'));
    })
    .filter((entity) => Object.keys(entity).length > 1);
  data.bankAccountLevelOverrides = (data.bankAccountLevelOverrides ?? [])
    .map((bankAccount) => {
      return Object.fromEntries(
        Object.entries(bankAccount).filter(([k, v]) => k === 'bankAccountId' || v !== 'default')
      );
    })
    .filter((entity) => Object.keys(entity).length > 1);
  return data;
};

export const PageCreateRole: React.FC = () => {
  const { currentPlatform } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const createRole = useCreateCustomRole({
    onSuccess: () => {
      addSuccessNotification({ content: 'Role created successfully' });
      navigate(ROUTE.PLATFORM_ROLES);
    },
    onError: (error) => {
      addDangerNotification({ content: error.message });
    },
  });
  const updateRole = useUpdateCustomRole({
    onSuccess: (response) => {
      addSuccessNotification({
        content: response.pendingApproval
          ? 'Role created. Live permissions submitted for approval.'
          : 'Role created successfully',
      });
      navigate(ROUTE.PLATFORM_ROLES);
    },
    onError: (error) => {
      addDangerNotification({ content: error.message });
    },
  });
  const sandboxEntitiesAndAccounts = useEntitiesAndAccounts({ mode: Mode.Sandbox });
  const liveEntitiesAndAccounts = useEntitiesAndAccounts({ mode: Mode.Live });

  const [visibleSection, setVisibleSection] = useState<Section>(Section.Info);
  const visiblePermissionSection =
    visibleSection === Section.Transfers
      ? PermissionConfigurationSection.Transfers
      : visibleSection === Section.Additional
        ? PermissionConfigurationSection.Additional
        : null;

  useEffect(() => {
    if (!sectionValues.includes(visibleSection)) {
      setVisibleSection(Section.Info);
    }
  }, [visibleSection]);

  const handleGoForward = useCallback(() => {
    setVisibleSection((section) => (section === Section.Info ? Section.Transfers : Section.Additional));
  }, []);

  const form = useForm<CreateCustomRoleRequestObject>({
    defaultValues: {
      name: '',
      description: '',
      platformLevelPermissions: {
        achCredits: 'read',
        achDebits: 'read',
        achReturns: 'read',
        apiKeys: 'none',
        entities: 'read',
        loans: 'read',
        platformInfo: 'none',
        realtimeTransfers: 'read',
        wireTransfers: 'read',
        depositChecks: 'read',
        issueChecks: 'read',
        bookTransfers: 'read',
        stopPayments: 'read',
        counterparties: 'read',
        bankAccounts: 'read',
        reporting: 'read',
        transferTemplates: 'read',
        webhooks: 'read',
      },
    },
  });

  const handleSubmit = form.handleSubmit((data: CreateCustomRoleRequestObject) => {
    const trimmedData = trimDefaults(data);

    // Due to some limitations in the API (we can't add both sandbox and live entities/accounts in the same request,
    // we must perform two updates to 1. set the sandbox entities/accounts then 2. set the live entities/accounts.
    if (currentPlatform?.isLiveEnabled) {
      const sandboxData = buildCreateRequestForEntitiesAndAccountsSubset(trimmedData, sandboxEntitiesAndAccounts);
      RoleRepository.create({ mode: 'sandbox', ...sandboxData })
        .then((resp) => updateRole.createRequest({ roleId: resp.id, mode: 'live', ...trimmedData }))
        .catch((err) => addDangerNotification({ content: err.message }));
    } else {
      createRole.createRequest(trimmedData);
    }
  });

  return (
    <>
      <StateTabs
        value={visibleSection}
        onValueChange={(section) => setVisibleSection(section as Section)}
        entries={sections}
      />
      <PageActions>
        {visibleSection !== Section.Additional && (
          <Button onClick={handleGoForward} variant="secondary">
            Next
            <Icon.ArrowRight />
          </Button>
        )}
        {visibleSection === Section.Additional && (
          <Button isLoading={!createRole.isInitialLoad && createRole.isLoading} onClick={handleSubmit} size="small">
            Save Role
          </Button>
        )}
      </PageActions>
      <FormProvider {...form}>
        {visibleSection === Section.Info && <RoleInfo />}
        {visiblePermissionSection && (
          <PermissionConfiguration
            visibleSection={visiblePermissionSection}
            sandboxEntitiesAndAccounts={sandboxEntitiesAndAccounts}
            liveEntitiesAndAccounts={liveEntitiesAndAccounts}
          />
        )}
      </FormProvider>
    </>
  );
};
